import React, { useEffect, useState } from 'react';
import { Row, Col } from "antd";
import { useParams, useLocation } from 'react-router-dom';
import { getCustomsOfficesByIata } from "../../../api's/airports";
import { useToken } from "../../../common/helper";
import { Table, Flex } from "antd";
import { CustomTitle, CustomParagraph } from "../../../components";
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../../components/GoogleMapsProvider';

const mapContainerStyle = {
  width: '100%',
  height: '208px',
  borderRadius: '12px',
  marginBottom: '24px',
  overflow: 'hidden',
};

const getLatLngFromOffice = (office) => {
  if (office && office.latitude && office.longitude) {
    return { lat: parseFloat(office.latitude), lng: parseFloat(office.longitude) };
  }
  // Default to JFK if not available
  return { lat: 40.6413, lng: -73.7781 };
};


const formatPhoneNumber = (number) => {
  if (!number) return "";
  const cleaned = number.replace(/[^\d]/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return number;
};

const isValidPhone = (value) => {
  if (!value) return false;
  const cleaned = value.replace(/[^\d]/g, "");
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === "1");
};

const cleanAddress = (address) => {
  if (!address) return "--";
  // Replace newlines with comma+space, collapse whitespace, then clean up commas
  let cleaned = address.replace(/\s*\n+\s*/g, ", ").replace(/\s+/g, " ").trim();
  // Remove duplicate commas and spaces around commas
  cleaned = cleaned.replace(/(, ?)+/g, ", "); // collapse multiple commas/spaces to one
  cleaned = cleaned.replace(/^, |, $/g, ""); // remove leading/trailing comma+space
  return cleaned;
};

const cleanForGeocode = (address) => {
  if (!address) return "";
  let cleaned = address.replace(/\s*\n+\s*/g, ", ").replace(/\s+/g, " ").trim();
  if (!/usa|united states/i.test(cleaned)) {
    cleaned += ", USA";
  }
  return cleaned;
};

const geocodeAddress = async (address, apiKey) => {
  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
  );
  const data = await response.json();
  if (data.status === "OK") {
    const { lat, lng } = data.results[0].geometry.location;
    return { lat, lng };
  }
  return null;
};

const CustomsOffice = () => {
  const { code } = useParams();
  const location = useLocation();
  const { token } = useToken();
  const [customs, setCustoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [officeLatLngs, setOfficeLatLngs] = useState({});

  const { isLoaded } = useGoogleMaps();
  const firstOffice = customs[0];
  const mapCenter = firstOffice ? getLatLngFromOffice(firstOffice) : { lat: 40.6413, lng: -73.7781 };
  const [activeInfoIdx, setActiveInfoIdx] = useState(null);

  useEffect(() => {
    getCustomsOfficesByIata(code, token)
      .then((data) => {
        setCustoms(data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    const fetchLatLngs = async () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const latLngs = {};
      for (const office of customs) {
        if (office.latitude && office.longitude) {
          latLngs[office.port_code] = {
            lat: parseFloat(office.latitude),
            lng: parseFloat(office.longitude),
          };
        } else if (office.location_address) {
          const coords = await geocodeAddress(cleanForGeocode(office.location_address), apiKey);
          console.log(coords, cleanForGeocode(office.location_address))
          if (coords) latLngs[office.port_code] = coords;
        }
      }
      setOfficeLatLngs(latLngs);
    };
    if (customs.length > 0) fetchLatLngs();
  }, [customs]);

  if (loading) return <div>Loading...</div>;

  return (
    <section className="airline-list">
      <Flex vertical gap={16}>
        <CustomTitle
          text={`${location.state?.airportName || location.state?.iataCode || code} – Customs Offices`}
          fontSize="24px"
          fontWeight="700"
        />
        {customs.length > 0 && customs[0].port_code && (
          <CustomParagraph
            text={`Port Code: ${customs[0].port_code}`}
            fontSize={16}
            fontWeight={500}
            color="#000"
            style={{ marginBottom: 8 }}
          />
        )}
        {customs.map((office, idx) => {
          const validTypes = ["phone", "fax", "email"];
          const isValidEmail = (value) =>
            !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

          const filteredContacts = (office.contacts || []).filter((contact) => {
            const type = contact.contact_type?.toLowerCase();
            if (!validTypes.includes(type)) return false;
            if (!contact.contact_value || !contact.contact_value.trim()) return false;
            if (type === "phone" || type === "fax") {
              return isValidPhone(contact.contact_value);
            }
            if (type === "email") {
              return isValidEmail(contact.contact_value);
            }
            return false;
          });

          const grouped = filteredContacts.reduce((acc, contact) => {
            if (!acc[contact.contact_title]) acc[contact.contact_title] = [];
            acc[contact.contact_title].push(contact);
            return acc;
          }, {});

          const contactTableData = Object.entries(grouped).flatMap(([title, contacts]) =>
            contacts.map((contact, i) => ({
              key: `${title}-${i}`,
              title,
              type: contact.contact_type,
              value: contact.contact_value,
            }))
          );

          const contactColumns = [
            {
              title: "Title",
              dataIndex: "title",
              key: "title",
              width: 120,
              render: (text, row, idx) =>
                idx === 0 || contactTableData[idx - 1]?.title !== text ? (
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{text}</span>
                ) : null,
            },
            {
              title: "Type",
              dataIndex: "type",
              key: "type",
              width: 100,
              render: (text) => (
                <span style={{ fontWeight: 500, fontSize: 12, color: "#000" }}>{text}</span>
              ),
            },
            {
              title: "Value",
              dataIndex: "value",
              key: "value",
              render: (value, row) => (
                <span style={{ fontSize: 12, color: "#4B4B4B" }}>
                  {row.type?.toLowerCase() === "email" ? (
                    <a href={`mailto:${value}`} style={{ color: "#000", textDecoration: "underline" }}>{value}</a>
                  ) : row.type?.toLowerCase() === "phone" || row.type?.toLowerCase() === "fax" ? (
                    formatPhoneNumber(value)
                  ) : (
                    value
                  )}
                </span>
              ),
            },
          ];
          const latLng = officeLatLngs[office.port_code] || { lat: 40.6413, lng: -73.7781 };

          // ...contactTableData and contactColumns logic...

          return (
            <div
              key={idx}
              className="customs-card"
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: 24,
                marginBottom: 24,
                boxShadow: "0 2px 8px #f0f1f2",
              }}
            >
              <Row gutter={32}>
                {/* LEFT: Contacts */}
                <Col xs={24} md={14}>
                  <CustomTitle text="Contacts" fontSize="16px" fontWeight="600" />
                  {contactTableData.length > 0 ? (
                    <Table
                      columns={contactColumns}
                      dataSource={contactTableData}
                      pagination={false}
                      bordered={false}
                      showHeader={false}
                      rowClassName={() => "airline-contacts-table"}
                      style={{ background: "#fff" }}
                    />
                  ) : (
                    <CustomParagraph text="No contacts available." fontSize={12} color="#888" />
                  )}
                </Col>
                {/* RIGHT: Map, Addresses, Office Hours */}
                <Col xs={24} md={10}>
                  {isLoaded && (
                    <div style={{ ...mapContainerStyle, marginBottom: 16 }}>
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "208px" }}
                        center={latLng}
                        zoom={12}
                      >
                        <Marker position={latLng} />
                      </GoogleMap>
                    </div>
                  )}
                  {cleanAddress(office.location_address) !== "--" && (
                    <CustomParagraph
                      text={`Location: ${cleanAddress(office.location_address)}`}
                      fontSize={14}
                      fontWeight={400}
                      color="#000"
                    />
                  )}
                  {cleanAddress(office.mailing_address) !== "--" && (
                    <CustomParagraph
                      text={`Mailing: ${cleanAddress(office.mailing_address)}`}
                      fontSize={14}
                      fontWeight={400}
                      color="#000"
                    />
                  )}
                  <div style={{ marginTop: 16 }}>
                    <CustomTitle text="Office Hours" fontSize="16px" fontWeight="600" />
                    {office.office_hours && office.office_hours.length > 0 ? (
                      <Table
                        columns={[
                          { title: "Day", dataIndex: "day", key: "day", width: 80, render: (text) => <span style={{ fontWeight: 500, fontSize: 12 }}>{text}</span> },
                          { title: "Open", dataIndex: "open_time", key: "open_time", width: 90, render: (text) => <span style={{ fontSize: 12 }}>{text || "--"}</span> },
                          { title: "Close", dataIndex: "close_time", key: "close_time", width: 90, render: (text) => <span style={{ fontSize: 12 }}>{text || "--"}</span> },
                          { title: "Timezone", dataIndex: "timezone", key: "timezone", width: 90, render: (text) => <span style={{ fontSize: 12 }}>{text || "--"}</span> },
                        ]}
                        dataSource={office.office_hours.map((hour, i) => ({ ...hour, key: i }))}
                        pagination={false}
                        bordered={false}
                        showHeader={false}
                        style={{ background: "#fff" }}
                      />
                    ) : (
                      <CustomParagraph text="No office hours available." fontSize={12} color="#888" />
                    )}
                  </div>
                </Col>
              </Row>
              {/* Notices across the bottom */}
              {office.notices && (
                <div style={{ marginTop: 24 }}>
                  <CustomParagraph
                    text={`Notices: ${office.notices}`}
                    fontSize={14}
                    fontWeight={400}
                    color="#000"
                    style={{ background: "#f6f6f6", padding: 12, borderRadius: 8 }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </Flex>
    </section>
  );
};

export default CustomsOffice;